/**
 * Created by pyt on 1/20/17.
 */
import { capitalize } from 'vue';

import i18n from '@/vueI18n';

export interface IMediaTypeAPI {
  mediatype_id: number;
  enumeration_id: number;
  name: string;
  code: string;
  children?: IMediaTypeAPI[];
  parent_mediatype_id?: number;
}

export default class MediaType {
  constructor(
    public id: number,
    public enumerationId: number,
    private _name: string,
    public code: string,
    public children: MediaType[],
    public parentId: number,
  ) {
    // sometimes codes are returned with '.' at the end
    if (this.code.endsWith('.')) {
      this.code = this.code.slice(0, this.code.length - 1);
    }
  }

  static fromApi(apiObject: IMediaTypeAPI): MediaType {
    return new MediaType(
      apiObject.mediatype_id,
      apiObject.enumeration_id,
      apiObject.name,
      apiObject.code,
      (apiObject.children || []).map(MediaType.fromApi),
      apiObject.parent_mediatype_id,
    );
  }

  set name(name: string) {
    this._name = name;
  }

  get name(): string {
    const levels = this._name.replace(/[^>]/g, '').length;
    return this.code
      .split('.')
      .slice(-1 - levels)
      .map((c) => c.replaceAll('_', '').toLowerCase())
      .map((c) => capitalize(i18n.global.t(`options.venueTypes.${c}`)))
      .join(' > ');
  }

  get rawName(): string {
    return this._name;
  }

  get formattedName(): string {
    const nameCode = this.code.split('.').pop().replaceAll('_', '').toLowerCase();
    return capitalize(i18n.global.t(`options.venueTypes.${nameCode}`));
  }

  get formattedFullName(): string {
    // Format venue type code from something like "transit.subway.platform" to "Transit > Subway > Platform"
    const splitCodes = this.code.replaceAll('_', '').toLowerCase().split('.');
    const formattedNames = splitCodes.map((e) => capitalize(i18n.global.t(`options.venueTypes.${e}`)));
    return formattedNames.join(' > ');
  }

  get displayName(): string {
    // Format grand-children venue type only to a format like: "Child > Grand-child"
    if (this.code.split('.').length === 3) {
      const splitCodes = this.code.replaceAll('_', '').toLowerCase().split('.').splice(-2);
      const formattedNames = splitCodes.map((e) => capitalize(i18n.global.t(`options.venueTypes.${e}`)));
      return formattedNames.join(' > ');
    }
    return this.formattedName;
  }

  copy(): MediaType {
    return MediaType.fromApi(this.toApi());
  }

  toApi(): IMediaTypeAPI {
    return {
      mediatype_id: this.id >= 0 ? this.id : undefined,
      enumeration_id: this.enumerationId,
      name: this.name,
      code: this.code,
      children: this.children.map((x) => x.toApi()),
      parent_mediatype_id: this.parentId,
    };
  }
}
